import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { queryClient } from '../app/queryClient';
import { getBtc15Predictor } from '../lib/apiClient';
import { hasLocalHistory } from '../lib/btc15History';

const POLL_INTERVAL_MS = 15_000;
const WARMUP_DELAY_MS = 1_500;

const predictorQuery = {
  queryKey: ['btc15-predictor'],
  queryFn: getBtc15Predictor,
  staleTime: POLL_INTERVAL_MS,
} as const;

export function useBtc15Predictor() {
  return useQuery({
    ...predictorQuery,
    refetchInterval: POLL_INTERVAL_MS,
    // Default (refetchIntervalInBackground: false): polling pauses while the
    // tab is hidden, so a backgrounded phone doesn't keep hammering the API.
    // Retry behavior comes from the app-wide QueryClient default (queryClient.ts).
  });
}

/**
 * Fetches the current round shortly after the app opens, for someone who
 * has used the Predictor on this device before, so the page opens on real
 * data (then refreshes) instead of a second or two of placeholders. One
 * request per app open; anyone who has never opened the Predictor is left
 * alone.
 */
export function usePredictorWarmup() {
  useEffect(() => {
    if (!hasLocalHistory()) return;
    const id = window.setTimeout(() => {
      // Best-effort: a failure here just means the page loads as before.
      void queryClient.prefetchQuery(predictorQuery).catch(() => {});
    }, WARMUP_DELAY_MS);
    return () => window.clearTimeout(id);
  }, []);
}
